import { IResult, IShowSelectedLaneParam } from "@/types/map";
import * as turf from "@turf/helpers";
import length from "@turf/length";
import lineSlice from "@turf/line-slice";
import lineSliceAlong from "@turf/line-slice-along";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { loadModules } from "esri-loader";

interface ILaneInfo {
  path: number[][];
  startCross: string;
  endCross: string;
  startPosition?: number;
  endPosition?: number;
}

export default class SelectLane {
  private static intances: Map<string, SelectLane>;

  public static getInstance(view: __esri.SceneView) {
    const id = view.container.id;
    if (!SelectLane.intances) {
      SelectLane.intances = new Map();
    }
    let instance = SelectLane.intances.get(id);
    if (!instance) {
      instance = new SelectLane(view);
      SelectLane.intances.set(id, instance);
    }
    return instance;
  }

  private view: __esri.SceneView;

  constructor(view: __esri.SceneView) {
    this.view = view;
  }

  private laneLayer!: __esri.FeatureLayer;
  private gridLayer!: __esri.GraphicsLayer;
  private Graphic!: __esri.GraphicConstructor;

  private laneMap: Map<string, ILaneInfo> = new Map();

  /** 两个格子之间的间隙 */
  private readonly gapLength = 0.5;

  private initialized = false;

  public async initialize() {
    if (!this.initialized) {
      type MapModules = [
        typeof import("esri/Graphic"),
        typeof import("esri/layers/FeatureLayer"),
        typeof import("esri/layers/GraphicsLayer")
      ];
      const [Graphic, FeatureLayer, GraphicsLayer] = await (loadModules([
        "esri/Graphic",
        "esri/layers/FeatureLayer",
        "esri/layers/GraphicsLayer",
      ]) as Promise<MapModules>);

      this.Graphic = Graphic;

      this.laneLayer = new FeatureLayer({
        url:
          "http://115.28.88.187:6080/arcgis/rest/services/Unicron/ShangHai_Road/MapServer/7",
      });
      this.gridLayer = new GraphicsLayer();
      this.view.map.add(this.gridLayer);

      this.view.on("click", async (event: MouseEvent) => {
        await this.graphicClick(event);
      });
      this.initialized = true;
    }

    this.gridLayer.removeAll();
    await this.showLanes();
    // this.laneMap.clear();
    this.laneMap.forEach((laneInfo, laneId) => {
      laneInfo.startPosition = undefined;
      laneInfo.endPosition = undefined;
    });
  }

  private async showLanes() {
    const laneGraphics = (
      await this.laneLayer.queryFeatures(this.laneLayer.createQuery())
    ).features;
    laneGraphics.forEach((graphic) => {
      if (
        graphic.getAttribute("START_CROSS") !== "" &&
        graphic.getAttribute("END_CROSS") !== ""
      ) {
        // 车道中心线在进口道。如果按行车方向切割，需要倒序。
        const lanePath = (graphic.geometry as __esri.Polyline).paths[0].reverse();
        const laneId = graphic.getAttribute("FEATUREID");
        const startCross = graphic.getAttribute("START_CROSS");
        const endCross = graphic.getAttribute("END_CROSS");
        this.laneMap.set(laneId, { path: lanePath, startCross, endCross });

        graphic.symbol = this.getLaneSymbol(false);
        this.gridLayer.add(graphic);
      }
    });
  }

  private getLaneSymbol(selected: boolean): any {
    return {
      type: "line-3d",
      symbolLayers: [
        {
          type: "path",
          profile: "quad",
          width: 1.5,
          height: 0.5,
          material: {
            // 封路路段为红色，
            color: selected ? [255, 68, 68, 0.8] : [51, 181, 229, 0.8],
          },
          cap: "butt",
          profileRotation: "heading",
          castShadows: false,
        },
      ],
    };
  }

  private async graphicClick(event: any) {
    const response = await this.view.hitTest(event, {
      include: [this.gridLayer],
    });
    if (response.results.length > 0) {
      const graphic = response.results[0].graphic;
      const laneId = graphic.getAttribute("FEATUREID");
      this.clearGraphicInLane(laneId);

      const laneInfo = this.laneMap.get(laneId);
      if (laneInfo) {
        const laneTurfLine = turf.lineString(laneInfo.path);
        const totalLength = length(laneTurfLine, { units: "meters" });
        // 车道线的起点
        const firstTurfPoint = turf.point(laneInfo.path[0]);

        // 点击位置坐标
        const clickPoint: __esri.Point = event.mapPoint;
        // 点击位置在车道中心线上的投影
        const clickTurfPoint = turf.point([
          clickPoint.longitude,
          clickPoint.latitude,
        ]);
        const nearestPoint = nearestPointOnLine(laneTurfLine, clickTurfPoint);
        const slicedLine = lineSlice(
          firstTurfPoint,
          nearestPoint,
          laneTurfLine
        );
        // 点击位置和起点的距离
        const slicedLength = Number(
          length(slicedLine, { units: "meters" }).toFixed(2)
        );

        // 第一次点击，车道还没有被切割，将车道在点击点切割为两份
        if (!laneInfo.startPosition) {
          // 两段线都缩短一点，空出一个间隙
          const firstTurfLine = lineSliceAlong(
            laneTurfLine,
            0,
            slicedLength - this.gapLength,
            { units: "meters" }
          );
          const firstGraphic = new this.Graphic({
            geometry: {
              type: "polyline",
              paths: [firstTurfLine.geometry.coordinates],
            } as any,
            symbol: this.getLaneSymbol(false),
            attributes: { FEATUREID: laneId },
          });
          const secondTurfLine = lineSliceAlong(
            laneTurfLine,
            slicedLength + this.gapLength,
            totalLength,
            { units: "meters" }
          );
          const secondGraphic = new this.Graphic({
            geometry: {
              type: "polyline",
              paths: [secondTurfLine.geometry.coordinates],
            } as any,
            symbol: this.getLaneSymbol(false),
            attributes: { FEATUREID: laneId },
          });
          this.gridLayer.addMany([firstGraphic, secondGraphic]);
          laneInfo.startPosition = slicedLength;
        }
        // 第二次点击
        else if (!laneInfo.endPosition) {
          if (slicedLength < laneInfo.startPosition) {
            // 点击在上一个分隔点之前
            laneInfo.endPosition = laneInfo.startPosition;
            laneInfo.startPosition = slicedLength;
          } else {
            // 点击在上一个分割点之后
            laneInfo.endPosition = slicedLength;
          }

          this.drawSlicedLane(
            laneId,
            laneTurfLine,
            totalLength,
            laneInfo.startPosition,
            laneInfo.endPosition
          );
        } else {
          // 之后点击修改起终点
          // 起点终点之间的中点距离
          const mediumPosition =
            laneInfo.startPosition +
            (laneInfo.endPosition - laneInfo.startPosition) / 2;
          if (slicedLength > mediumPosition) {
            laneInfo.endPosition = slicedLength;
          } else {
            laneInfo.startPosition = slicedLength;
          }
          this.drawSlicedLane(
            laneId,
            laneTurfLine,
            totalLength,
            laneInfo.startPosition,
            laneInfo.endPosition
          );
        }
      }
    }
  }

  /**
   * 清除某个车道上所有的graphic
   * @param laneId 车道号
   */
  private clearGraphicInLane(laneId: string): void {
    const graphics = this.gridLayer.graphics.filter(
      (graphic) => graphic.getAttribute("FEATUREID") === laneId
    );

    this.gridLayer.removeMany(graphics.toArray());
  }

  /**
   * 将整条车道按照起点距离和终点距离分隔成三段
   * @param laneId 车道号
   * @param laneTurfLine 车道线
   * @param totalLength 车道长度
   * @param startPosition 起点距离
   * @param endPosition 终点距离
   */
  private drawSlicedLane(
    laneId: string,
    laneTurfLine: turf.Feature<turf.LineString, turf.Properties>,
    totalLength: number,
    startPosition: number,
    endPosition: number
  ): void {
    // 重新分割
    const firstTurfLine = lineSliceAlong(laneTurfLine, 0, startPosition - 0.2, {
      units: "meters",
    });
    const firstGraphic = new this.Graphic({
      geometry: {
        type: "polyline",
        paths: [firstTurfLine.geometry.coordinates],
      } as any,
      symbol: this.getLaneSymbol(false),
      attributes: { FEATUREID: laneId },
    });

    const secondTurfLine = lineSliceAlong(
      laneTurfLine,
      startPosition + this.gapLength,
      endPosition - this.gapLength,
      { units: "meters" }
    );
    const secondGraphic = new this.Graphic({
      geometry: {
        type: "polyline",
        paths: [secondTurfLine.geometry.coordinates],
      } as any,
      symbol: this.getLaneSymbol(true),
      attributes: { FEATUREID: laneId },
    });

    const thirdTurfLine = lineSliceAlong(
      laneTurfLine,
      endPosition + this.gapLength,
      totalLength,
      { units: "meters" }
    );
    const thirdGraphic = new this.Graphic({
      geometry: {
        type: "polyline",
        paths: [thirdTurfLine.geometry.coordinates],
      } as any,
      symbol: this.getLaneSymbol(false),
      attributes: { FEATUREID: laneId },
    });

    this.gridLayer.addMany([firstGraphic, secondGraphic, thirdGraphic]);
  }

  public getResult(): IResult {
    const selectedLanes: Map<
      string,
      { laneIds: string[]; startPosition: number; endPosition: number }
    > = new Map();

    // 将选中的车道按照"上游路口-下游路口"分组
    this.laneMap.forEach((laneInfo, laneId) => {
      if (laneInfo.startPosition && laneInfo.endPosition) {
        const laneDir = laneInfo.startCross + "-" + laneInfo.endCross;
        const groupedLanes = selectedLanes.get(laneDir);
        if (groupedLanes) {
          groupedLanes.laneIds.push(laneId);
          groupedLanes.startPosition = Math.min(
            groupedLanes.startPosition,
            laneInfo.startPosition
          );
          groupedLanes.endPosition = Math.max(
            groupedLanes.endPosition,
            laneInfo.endPosition
          );
        } else {
          selectedLanes.set(laneDir, {
            laneIds: [laneId],
            startPosition: laneInfo.startPosition,
            endPosition: laneInfo.endPosition,
          });
        }
      }
    });

    const result: IShowSelectedLaneParam[] = [];
    selectedLanes.forEach((groupedLanes, laneDir) => {
      const crosses = laneDir.split("-");
      result.push({
        fromCrossId: crosses[0],
        toCrossId: crosses[1],
        distance2UpCross: groupedLanes.startPosition,
        length: groupedLanes.endPosition - groupedLanes.startPosition,
        gisLanes: groupedLanes.laneIds,
      });
    });
    return { status: 0, message: "", result };
  }

  public showSelectedLanes(params: IShowSelectedLaneParam[]): void {
    for (const param of params) {
      for (const laneId of param.gisLanes) {
        const lanePath = this.laneMap.get(laneId)?.path;
        if (!lanePath) {
          continue;
        }

        this.clearGraphicInLane(laneId);

        const laneTurfLine = turf.lineString(lanePath);
        const totalLength = length(laneTurfLine, { units: "meters" });
        this.drawSlicedLane(
          laneId,
          laneTurfLine,
          totalLength,
          param.distance2UpCross,
          param.distance2UpCross + param.length
        );
      }
    }
  }
}
